import React, {useEffect, useMemo, useRef, useState} from "react";
import {BreadCrumb} from "primereact/breadcrumb";
import {Dropdown} from "primereact/dropdown";
import {InputSwitch} from "primereact/inputswitch";
import {ProgressSpinner} from "primereact/progressspinner";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {useGoal} from "../../../models/goal/use-goal";
import Avatar from "../../../system/components/avatar";
import Icon from "../../../system/components/icon";
import {setMessage, setWorkspaceMembership} from "../../../system/app-slice";
import {upsertWorkspaceGoal} from "../../../system/workspace-graph-slice";
import {
    CLOSED_GOAL_STATUSES,
    GOAL_STATUS,
    OPEN_GOAL_STATUSES,
    REMINDER_FREQUENCIES,
    formatPersianDate,
    goalUpdateApi,
    linesToList,
    listToLines,
} from "../../../models/goal-update/goal-update-api";
import {StatusChip} from "./goal-update-view-dialog";
import "./goal-updates.css";
import {RichTextEditor, RichTextView, isBlankRichText} from "../../../system/components/rich-text";

// «بروزرسانی هدف» page (docs/tasks/17-goal-updates.md, design zofino-goal-update-ai-analysis.html): reminder, AI analysis,
// status, progress, people, summary / achievements / challenges / next steps and a live preview. Submitting notifies
// everyone under the goal.

const CYCLE_LABELS = {Q1: "فصل اول", Q2: "فصل دوم", Q3: "فصل سوم", Q4: "فصل چهارم", H1: "شش‌ماهه اول", H2: "شش‌ماهه دوم", Y: "سال"};
const ANALYSIS_STEPS = ["بررسی پیشرفت و اهداف زیرمجموعه", "تحلیل پروژه‌ها و کارهای متصل", "شناسایی ریسک‌ها و موانع", "مقایسه با بروزرسانی‌های قبلی", "ساخت پیشنهاد نهایی"];
const AI_ERRORS = {
    "ai.error.provider_unavailable": "سرویس هوش مصنوعی در حال حاضر در دسترس نیست.",
    "ai.error.engine_disabled": "هوش مصنوعی برای این فضای کاری فعال نیست.",
    "ai.error.insufficient_credits": "اعتبار هوش مصنوعی این فضای کاری کافی نیست.",
    "ai.error.rate_limited": "تعداد درخواست‌ها زیاد است؛ کمی بعد دوباره تلاش کنید.",
};
const fa = (value) => Number(value || 0).toLocaleString("fa-IR");

function cycleLabel(cycle) {
    if (!cycle?.range) return "بدون بازه";
    return `${CYCLE_LABELS[cycle.range] || cycle.range} ${cycle.year ? Number(cycle.year).toLocaleString("fa-IR", {useGrouping: false}) : ""}`;
}

export function AiAnalysis({goal, onApply, disabled}) {
    const [state, setState] = useState("idle");
    const [step, setStep] = useState(0);
    const [result, setResult] = useState(null);
    const [applied, setApplied] = useState(false);
    const timer = useRef(null);

    useEffect(() => () => clearInterval(timer.current), []);

    const run = async () => {
        setState("loading");
        setApplied(false);
        setStep(0);
        clearInterval(timer.current);
        timer.current = setInterval(() => setStep((value) => Math.min(value + 1, ANALYSIS_STEPS.length - 1)), 900);
        try {
            setResult(await goalUpdateApi.analyze(goal.documentId));
            setState("done");
        } catch (error) {
            setResult({analysis: null, metrics: null, error: "failed"});
            setState("done");
        } finally {
            clearInterval(timer.current);
        }
    };

    const analysis = result?.analysis;
    const metrics = result?.metrics;
    const toneColor = analysis?.tone === "good" ? "#34d399" : analysis?.tone === "danger" ? "#f87171" : "#fbbf24";
    const behind = metrics && metrics.progress !== null && metrics.expectedProgress !== null ? metrics.expectedProgress - metrics.progress : null;

    return (
        <section className="gu-ai p-4 md:p-5">
            <div className="flex flex-column lg:flex-row lg:align-items-center justify-content-between gap-4">
                <div className="flex align-items-start gap-3">
                    <div className="gu-ai__icon"><Icon icon="mi mi:psychology" className="text-3xl"/></div>
                    <div>
                        <div className="flex align-items-center gap-2 mb-1">
                            <span className="text-xs font-bold" style={{color: "#e6531e"}}>تحلیل هوشمند هدف</span>
                            <span className="text-2xs gu-ai__muted px-2 border-round-2xl" style={{border: "1px solid rgba(255,255,255,.1)"}}>AI</span>
                        </div>
                        <h2 className="m-0 text-lg font-bold">قبل از بروزرسانی، تصویر کامل هدف را ببینید</h2>
                        <p className="m-0 mt-2 text-sm gu-ai__muted" style={{lineHeight: 1.9, maxWidth: "48rem"}}>
                            هوش مصنوعی پیشرفت، اهداف زیرمجموعه، پروژه‌های متصل، کارهای عقب‌افتاده، ریسک‌ها، زمان باقی‌مانده و بروزرسانی‌های قبلی هدف را بررسی می‌کند و یک تحلیل قابل استفاده برای تصمیم‌گیری ارائه می‌دهد.
                        </p>
                    </div>
                </div>
                <button type="button" className="gu-ai__button" disabled={state === "loading" || disabled} onClick={run}>
                    <Icon icon={state === "loading" ? "mi mi:progress_activity" : state === "done" ? (result?.analysis ? "mi mi:check_circle" : "mi mi:refresh") : "mi mi:auto_awesome"} className={`text-xl ${state === "loading" ? "pi-spin" : ""}`}/>
                    {state === "loading" ? "در حال تحلیل..." : state === "done" ? (result?.analysis ? "تحلیل انجام شد" : "تلاش دوباره") : "تحلیل کامل هدف"}
                </button>
            </div>

            {state === "loading" && (
                <div className="flex flex-column align-items-center gap-2 py-5 mt-4" style={{borderTop: "1px solid rgba(255,255,255,.1)"}}>
                    <div className="flex gap-2"><span className="gu-ai__dot"/><span className="gu-ai__dot"/><span className="gu-ai__dot"/></div>
                    <strong className="text-sm">در حال تحلیل تمام داده‌های هدف...</strong>
                    <span className="text-xs gu-ai__muted">{ANALYSIS_STEPS[step]}</span>
                </div>
            )}

            {state === "done" && (
                <div className="flex flex-column gap-4 mt-4 pt-4" style={{borderTop: "1px solid rgba(255,255,255,.1)"}}>
                    {analysis ? (
                        <div className="flex flex-column md:flex-row md:align-items-center justify-content-between gap-3">
                            <div>
                                <p className="m-0 mb-1 text-2xs gu-ai__muted">نتیجه تحلیل</p>
                                <div className="flex align-items-center gap-2">
                                    <span style={{width: ".6rem", height: ".6rem", borderRadius: 999, background: toneColor, boxShadow: `0 0 10px ${toneColor}`}}/>
                                    <h3 className="m-0 text-base font-bold">{analysis.headline}</h3>
                                </div>
                            </div>
                            <span className="text-xs font-bold px-3 py-2 border-round-lg" style={{color: toneColor, background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.1)"}}>اطمینان تحلیل: {fa(analysis.confidence)}٪</span>
                        </div>
                    ) : (
                        <div className="text-sm p-3 border-round-xl" style={{background: "rgba(251,191,36,.08)", color: "#fcd34d"}}>
                            {AI_ERRORS[result?.error] || "تحلیل هوش مصنوعی انجام نشد."} {metrics ? "شاخص‌های محاسبه‌شده هدف را در زیر می‌بینید." : ""}
                        </div>
                    )}

                    {metrics && (
                        <div className="grid m-0">
                            {[
                                {label: "پیشرفت فعلی", value: metrics.progress === null ? "—" : `${fa(metrics.progress)}٪`, note: behind === null ? (metrics.expectedProgress === null ? "" : "") : behind > 0 ? `${fa(behind)}٪ کمتر از انتظار` : "مطابق یا جلوتر از انتظار", noteColor: behind > 0 ? "#fcd34d" : "#6ee7b7"},
                                {label: "زمان باقی‌مانده", value: metrics.daysLeft === null ? "—" : metrics.daysLeft < 0 ? "تمام شده" : `${fa(metrics.daysLeft)} روز`, note: metrics.totalDays ? `از ${fa(metrics.totalDays)} روز` : "بدون بازه یا سررسید"},
                                {label: "اهداف زیرمجموعه در ریسک", value: `${fa(metrics.subgoalsAtRisk)} از ${fa(metrics.subgoals)}`, note: metrics.subgoalsAtRisk ? "نیازمند اقدام" : "", noteColor: "#fcd34d"},
                                {label: "پروژه‌های متصل", value: fa(metrics.projects), note: metrics.projectsOffTrack ? `${fa(metrics.projectsOffTrack)} پروژه خارج از مسیر یا با کار عقب‌افتاده` : `${fa(metrics.overdueTasks)} کار عقب‌افتاده`, noteColor: metrics.projectsOffTrack ? "#fca5a5" : undefined},
                            ].map((tile) => (
                                <div key={tile.label} className="col-6 md:col-3 p-1">
                                    <div className="gu-ai__tile h-full">
                                        <p className="m-0 text-2xs gu-ai__muted">{tile.label}</p>
                                        <strong className="block text-2xl mt-2">{tile.value}</strong>
                                        {tile.note && <p className="m-0 mt-2 text-2xs" style={{color: tile.noteColor || "#9ca3af"}}>{tile.note}</p>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {analysis && (
                        <>
                            <div className="grid m-0">
                                <div className="col-12 lg:col-8 p-1">
                                    <div className="gu-ai__tile h-full">
                                        <h4 className="m-0 mb-2 flex align-items-center gap-2 text-sm"><span className="inline-flex flex-none" style={{color: "#e6531e"}}><Icon icon="mi mi:summarize" className="text-lg"/></span>جمع‌بندی هوش مصنوعی</h4>
                                        <p className="m-0 text-xs" style={{color: "#d1d5db", lineHeight: 2}}>{analysis.summary}</p>
                                    </div>
                                </div>
                                <div className="col-12 lg:col-4 p-1">
                                    <div className="gu-ai__tile gu-ai__accent h-full">
                                        <h4 className="m-0 mb-2 flex align-items-center gap-2 text-sm"><span className="inline-flex flex-none" style={{color: "#e6531e"}}><Icon icon="mi mi:crisis_alert" className="text-lg"/></span>مهم‌ترین هشدار</h4>
                                        <p className="m-0 text-xs" style={{color: "#d1d5db", lineHeight: 2}}>{analysis.mainWarning || "هشدار مهمی دیده نشد."}</p>
                                    </div>
                                </div>
                            </div>
                            <div className="grid m-0">
                                {[{title: "نقاط مثبت", icon: "trending_up", color: "#34d399", items: analysis.positives}, {title: "ریسک‌ها و موانع", icon: "warning", color: "#fbbf24", items: analysis.risks}].map((block) => (
                                    <div key={block.title} className="col-12 md:col-6 p-1">
                                        <div className="gu-ai__tile h-full">
                                            <h4 className="m-0 mb-3 flex align-items-center gap-2 text-xs"><span className="inline-flex flex-none" style={{color: block.color}}><Icon icon={`mi mi:${block.icon}`} className="text-lg"/></span>{block.title}</h4>
                                            {block.items?.length ? (
                                                <ul className="m-0 p-0 list-none flex flex-column gap-2 text-xs" style={{color: "#d1d5db"}}>
                                                    {block.items.map((item, index) => <li key={index} className="flex gap-2"><span className="gu-bullet" style={{background: block.color}}/><span style={{lineHeight: 1.9}}>{item}</span></li>)}
                                                </ul>
                                            ) : <span className="text-xs gu-ai__muted">موردی نیست.</span>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="gu-ai__tile gu-ai__accent flex flex-column md:flex-row md:align-items-center justify-content-between gap-3">
                                <div>
                                    <h4 className="m-0 flex align-items-center gap-2 text-sm"><span className="inline-flex flex-none" style={{color: "#e6531e"}}><Icon icon="mi mi:recommend" className="text-lg"/></span>پیشنهاد برای این بروزرسانی</h4>
                                    <p className="m-0 mt-2 text-xs" style={{color: "#d1d5db", lineHeight: 2}}>{analysis.suggestion?.text}</p>
                                </div>
                                <button type="button" className={`gu-ai__light-button ${applied ? "is-applied" : ""}`} disabled={disabled} onClick={() => { onApply(analysis.suggestion); setApplied(true); }}>
                                    {applied ? "در بروزرسانی اعمال شد" : "اعمال در بروزرسانی"}
                                </button>
                            </div>
                        </>
                    )}

                    <div className="flex align-items-center justify-content-between gap-2">
                        <p className="m-0 text-2xs" style={{color: "#6b7280"}}>تحلیل بر اساس داده‌های فعلی هدف، پروژه‌ها، اهداف زیرمجموعه و بروزرسانی‌های قبلی تهیه شده است.</p>
                        <button type="button" className="flex align-items-center gap-1 border-none bg-transparent text-xs font-bold cursor-pointer" style={{color: "#e6531e", font: "inherit"}} onClick={run}>
                            <Icon icon="mi mi:refresh" className="text-base"/>تحلیل دوباره
                        </button>
                    </div>
                </div>
            )}
        </section>
    );
}

function GoalUpdateStatus() {
    const params = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const goalApi = useGoal();
    const {workspaceMembershipData} = useSelector((state) => state.app);
    const [goal, setGoal] = useState(null);
    const [meta, setMeta] = useState(null);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const summaryRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [goalData, listData] = await Promise.all([goalApi.getGoal(params.id), goalUpdateApi.list(params.id)]);
                if (cancelled) return;
                const requested = searchParams.get("status");
                setGoal(goalData);
                setMeta(listData);
                setForm({
                    goalStatus: GOAL_STATUS[requested] ? requested : goalData?.goalStatus || "on-way",
                    progress: goalData?.progress ?? 0,
                    briff: "",
                    achievements: "",
                    challenges: "",
                    todos: "",
                });
            } catch (error) {
                if (!cancelled) setMeta({failed: true});
            }
        })();
        return () => { cancelled = true; };
        // the goal API hook has an unstable identity; reload only for another goal
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.id]);

    const canUpdate = Boolean(meta?.canUpdate);
    const change = (patch) => setForm((current) => ({...current, ...patch}));
    const preview = useMemo(() => form && ({
        ...form,
        achievements: linesToList(form.achievements),
        challenges: linesToList(form.challenges),
        todos: linesToList(form.todos),
    }), [form]);
    const empty = preview && isBlankRichText(preview.briff) && !preview.achievements.length && !preview.challenges.length && !preview.todos.length;

    const saveReminder = async (patch) => {
        const next = {...(meta?.reminder || {enabled: false, frequency: "weekly"}), ...patch};
        setMeta((current) => ({...current, reminder: next}));
        try {
            const saved = await goalUpdateApi.setReminder(goal.documentId, next);
            setMeta((current) => ({...current, reminder: saved}));
            dispatch(setMessage({severity: "success", content: saved.enabled ? "یادآوری بروزرسانی تنظیم شد." : "یادآوری بروزرسانی خاموش شد."}));
        } catch (error) {
            dispatch(setMessage({severity: "error", content: "ذخیره یادآوری انجام نشد."}));
        }
    };

    const applySuggestion = (suggestion) => {
        if (!suggestion) return;
        change({
            ...(suggestion.goalStatus && OPEN_GOAL_STATUSES.includes(suggestion.goalStatus) ? {goalStatus: suggestion.goalStatus} : {}),
            ...(suggestion.briff ? {briff: suggestion.briff} : {}),
            ...(suggestion.achievements?.length ? {achievements: listToLines(suggestion.achievements)} : {}),
            ...(suggestion.challenges?.length ? {challenges: listToLines(suggestion.challenges)} : {}),
            ...(suggestion.todos?.length ? {todos: listToLines(suggestion.todos)} : {}),
        });
        setTimeout(() => summaryRef.current?.scrollIntoView({behavior: "smooth", block: "center"}), 250);
    };

    const submit = async () => {
        if (saving || empty || !canUpdate) return;
        setSaving(true);
        try {
            const update = await goalUpdateApi.create(goal.documentId, {
                goalStatus: preview.goalStatus,
                progress: Math.max(0, Math.min(100, Math.round(Number(preview.progress) || 0))),
                briff: preview.briff,
                achievements: preview.achievements,
                challenges: preview.challenges,
                todos: preview.todos,
            });
            const nextGoal = {
                ...goal,
                goalStatus: update.goalStatus,
                progress: update.progress,
                briff: update.briff,
                achievements: update.achievements,
                challenges: update.challenges,
                todos: update.todos,
                lastUpdatedAt: update.createdAt,
            };
            dispatch(upsertWorkspaceGoal(nextGoal));
            const goals = workspaceMembershipData?.workspace?.goals || [];
            dispatch(setWorkspaceMembership({
                ...workspaceMembershipData,
                workspace: {...workspaceMembershipData.workspace, goals: goals.map((item) => (item.documentId === goal.documentId ? {...item, ...nextGoal} : item))},
            }));
            const notified = update.recipients?.length || 0;
            dispatch(setMessage({severity: "success", content: notified ? `بروزرسانی ثبت شد و برای ${fa(notified)} نفر اعلان رفت.` : "بروزرسانی ثبت شد."}));
            navigate(`/goals/${goal.id}`);
        } catch (error) {
            dispatch(setMessage({severity: "error", content: "ثبت بروزرسانی انجام نشد. دوباره تلاش کنید."}));
        } finally {
            setSaving(false);
        }
    };

    if (meta?.failed) {
        return <div className="h-full flex align-items-center justify-content-center text-sm">این هدف پیدا نشد یا به آن دسترسی ندارید.</div>;
    }
    if (!goal || !form || !meta) {
        return <div className="h-full flex align-items-center justify-content-center"><ProgressSpinner style={{width: "2.5rem", height: "2.5rem"}} strokeWidth="4"/></div>;
    }

    const reminder = meta.reminder || {enabled: false, frequency: "weekly"};
    const owner = goal.owner?.member;
    const author = workspaceMembershipData?.member || workspaceMembershipData?.workspace?.workspace_memberships?.find((item) => item.id === workspaceMembershipData?.id)?.member;
    const statusButton = (status) => {
        const config = GOAL_STATUS[status];
        const active = form.goalStatus === status;
        return (
            <div key={status} className="col-4 p-1">
                <button type="button" className={`gu-status gu-status--${config.tone} ${active ? "is-active" : ""}`} disabled={!canUpdate} onClick={() => change({goalStatus: status})} aria-pressed={active}>
                    <span className="gu-status__icon"><Icon icon={`mi mi:${config.icon}`} className="text-2xl"/></span>
                    <span className="text-xs font-bold">{config.label}</span>
                </button>
            </div>
        );
    };
    // `rich`: a long text with the rich text editor (the summary); the others are lists, one item per line
    const textField = (key, icon, label, placeholder, ref, rich = false) => (
        <div className="flex flex-column gap-2" ref={rich ? ref : undefined}>
            <label className="text-sm font-bold flex align-items-center gap-2"><Icon icon={`mi mi:${icon}`} className="text-lg text-400"/>{label}</label>
            {rich
                ? <RichTextEditor value={form[key]} placeholder={placeholder} readOnly={!canUpdate} ariaLabel={label} onChange={(html) => change({[key]: html})}/>
                : <textarea ref={ref} className="gu-textarea" value={form[key]} placeholder={placeholder} disabled={!canUpdate} onChange={(event) => change({[key]: event.target.value})}/>}
        </div>
    );

    return (
        <div className="gu h-full overflow-auto shabnam-font" dir="rtl" style={{background: "#fafafa"}}>
            <div className="p-3 md:p-4 flex flex-column gap-4" style={{maxWidth: "80rem", margin: "0 auto"}}>
                <BreadCrumb
                    separatorIcon={<Icon icon="mi mi:chevron_left text-xs"/>}
                    className="bg-transparent border-none text-xs p-0"
                    model={[{label: "اهداف", command: () => navigate("/goals")}, {label: goal.title, command: () => navigate(`/goals/${goal.id}`)}, {label: "بروزرسانی هدف"}]}
                />

                {!canUpdate && (
                    <div className="gu-banner text-sm">فقط مسئول هدف یا کسی که اجازه مدیریت اهداف دارد می‌تواند این هدف را بروزرسانی کند.</div>
                )}

                <div className="gu-reminder">
                    <div className="flex align-items-center gap-3">
                        <span className="inline-flex flex-none" style={{color: "#e6531e"}}><Icon icon="mi mi:notifications_active" className="text-xl"/></span>
                        <span className="text-sm font-bold">به من یادآوری کن بروزرسانی کنم</span>
                        <InputSwitch checked={Boolean(reminder.enabled)} disabled={!canUpdate} onChange={(event) => saveReminder({enabled: event.value})}/>
                    </div>
                    {reminder.enabled && (
                        <div className="flex align-items-center gap-2">
                            <span className="text-xs gu-muted">تکرار:</span>
                            <Dropdown
                                value={reminder.frequency || "weekly"}
                                options={REMINDER_FREQUENCIES}
                                optionLabel="label"
                                optionValue="value"
                                disabled={!canUpdate}
                                className="text-xs"
                                onChange={(event) => saveReminder({frequency: event.value})}
                            />
                        </div>
                    )}
                </div>

                <AiAnalysis goal={goal} disabled={!canUpdate} onApply={applySuggestion}/>

                <div className="grid m-0">
                    <section className="col-12 lg:col-8 p-0 lg:pl-3 flex flex-column gap-3">
                        <div className="gu-card p-3 w-fit">
                            <h1 className="m-0 text-xl font-bold">{goal.title}</h1>
                            <p className="m-0 mt-1 text-xs gu-muted flex align-items-center gap-1"><Icon icon="mi mi:calendar_today" className="text-sm"/>{formatPersianDate(new Date())}</p>
                        </div>
                        <div className="grid m-0">
                            <div className="col-12 md:col-6 p-0 md:pl-2 mb-2 md:mb-0">
                                <div className="gu-card p-3 flex align-items-center justify-content-between">
                                    <div>
                                        <p className="m-0 mb-1 text-2xs font-bold gu-muted">مسئول هدف</p>
                                        <p className="m-0 text-sm font-bold">{owner?.fullname || "—"}</p>
                                    </div>
                                    {owner && <Avatar memberData={owner} style={{width: "2.25rem", height: "2.25rem"}}/>}
                                </div>
                            </div>
                            <div className="col-12 md:col-6 p-0 md:pr-2">
                                <div className="gu-card p-3 flex align-items-center justify-content-between">
                                    <div>
                                        <p className="m-0 mb-1 text-2xs font-bold gu-muted">دوره زمانی</p>
                                        <p className="m-0 text-sm font-bold">{cycleLabel(goal.cycle)}</p>
                                    </div>
                                    <span className="flex align-items-center justify-content-center border-circle" style={{width: "2.25rem", height: "2.25rem", background: "#fff7ed", color: "#e6531e"}}><Icon icon="mi mi:schedule" className="text-xl"/></span>
                                </div>
                            </div>
                        </div>

                        <div className="gu-card p-4 md:p-5 flex flex-column gap-5">
                            <div className="flex flex-column gap-3">
                                <span className="gu-section-title">وضعیت و برچسب هدف</span>
                                <span className="gu-group-label">اهداف باز</span>
                                <div className="grid m-0">{OPEN_GOAL_STATUSES.map(statusButton)}</div>
                                <span className="gu-group-label">اهداف بسته</span>
                                <div className="grid m-0">{CLOSED_GOAL_STATUSES.map(statusButton)}</div>
                            </div>

                            <div className="flex flex-column gap-3">
                                <div className="flex align-items-center justify-content-between">
                                    <label className="text-sm font-bold" htmlFor="goal-update-progress">میزان پیشرفت</label>
                                    <div className="flex align-items-center gap-2">
                                        <input
                                            id="goal-update-progress"
                                            type="number"
                                            min={0}
                                            max={100}
                                            disabled={!canUpdate || meta.progressManaged}
                                            value={form.progress}
                                            onChange={(event) => change({progress: event.target.value === "" ? "" : Math.max(0, Math.min(100, Number(event.target.value)))})}
                                            className="text-center font-bold border-none border-round-xl p-2"
                                            style={{width: "4.5rem", background: "#f9fafb", color: "#e6531e", font: "inherit"}}
                                        />
                                        <span className="text-sm font-bold gu-muted">٪</span>
                                    </div>
                                </div>
                                <div className="gu-progress"><span style={{width: `${Math.max(0, Math.min(100, Number(form.progress) || 0))}%`}}/></div>
                                {/* a goal with a progress source keeps its computed percent (docs/tasks/28-goal-progress-source.md) */}
                                {meta.progressManaged && <span className="text-xs gu-muted">پیشرفت این هدف از «منبع پیشرفت» محاسبه می‌شود و اینجا تغییر نمی‌کند؛ از صفحه‌ی هدف ← «تنظیمات پیشرفت» قابل تنظیم است.</span>}
                            </div>

                            <div className="flex flex-column gap-3">
                                <label className="text-sm font-bold">همکاران هدف</label>
                                {meta.audience?.length ? (
                                    <div className="flex flex-wrap align-items-center gap-2">
                                        {meta.audience.slice(0, 12).map((person) => (
                                            <Avatar key={person.documentId || person.id} memberData={person.member} tooltip style={{width: "2.4rem", height: "2.4rem"}} className="text-2xs"/>
                                        ))}
                                        {meta.audience.length > 12 && <span className="text-xs gu-muted">+{fa(meta.audience.length - 12)}</span>}
                                        <span className="text-xs gu-muted mr-2">این بروزرسانی برای همه این افراد اعلان می‌شود.</span>
                                    </div>
                                ) : <span className="text-xs gu-muted">هنوز کسی زیر این هدف نیست؛ پروژه یا هدف زیرمجموعه وصل کنید.</span>}
                            </div>

                            <div className="flex flex-column gap-4 pt-4" style={{borderTop: "1px solid #f3f4f6"}}>
                                {textField("briff", "edit_note", "خلاصه وضعیت", "شرح مختصری از وضعیت فعلی بنویسید...", summaryRef, true)}
                                {textField("achievements", "emoji_events", "دستاوردها", "لیست موفقیت‌های این دوره... (هر مورد در یک خط)")}
                                {textField("challenges", "error", "موانع و چالش‌ها", "چه مواردی مانع پیشرفت هستند؟ (هر مورد در یک خط)")}
                                {textField("todos", "next_plan", "گام‌های بعدی", "برنامه عملیاتی برای دوره بعد... (هر مورد در یک خط)")}
                            </div>

                            <div className="flex justify-content-end gap-2 pt-2">
                                <button type="button" className="gu-soft-button gu-soft-button--plain" onClick={() => navigate(-1)}>انصراف</button>
                                <button type="button" className="gu-soft-button gu-soft-button--solid px-5" disabled={saving || empty || !canUpdate} onClick={submit} title={empty ? "حداقل یکی از بخش‌ها را پر کنید" : undefined} style={{opacity: saving || empty || !canUpdate ? .6 : 1}}>
                                    <Icon icon="mi mi:send" className="text-lg"/>{saving ? "در حال ثبت..." : "ثبت بروزرسانی"}
                                </button>
                            </div>
                        </div>
                    </section>

                    <aside className="col-12 lg:col-4 p-0 mt-3 lg:mt-0">
                        <div className="gu-preview">
                            <div className="flex align-items-center justify-content-between mb-3">
                                <h2 className="m-0 text-lg font-bold">پیش‌نمایش گزارش</h2>
                                <span className="inline-flex flex-none" style={{color: "#e6531e"}}><Icon icon="mi mi:visibility" className="text-xl"/></span>
                            </div>
                            <div className="gu-card overflow-hidden" style={{boxShadow: "0 12px 30px rgba(0,0,0,.08)"}}>
                                <div className="p-4" style={{background: "linear-gradient(135deg, rgba(230,83,30,.05), transparent)", borderBottom: "1px solid #f9fafb"}}>
                                    <div className="flex align-items-center justify-content-between mb-3">
                                        <StatusChip status={preview.goalStatus}/>
                                        <span className="text-2xl font-bold" style={{color: "#e6531e"}}>{fa(Number(preview.progress) || 0)}٪</span>
                                    </div>
                                    <h3 className="m-0 text-base font-bold" style={{lineHeight: 1.7}}>{goal.title}</h3>
                                    <div className="gu-progress mt-3" style={{height: ".35rem"}}><span style={{width: `${Math.max(0, Math.min(100, Number(preview.progress) || 0))}%`, boxShadow: "none"}}/></div>
                                </div>
                                <div className="p-4 flex flex-column gap-4" style={{maxHeight: "60vh", overflowY: "auto"}}>
                                    <div>
                                        <h4 className="m-0 mb-2 text-2xs font-bold gu-muted pr-2" style={{borderRight: "2px solid #e6531e"}}>خلاصه وضعیت</h4>
                                        <RichTextView className="m-0 text-xs" value={preview.briff} empty={<p className="m-0 text-xs text-300">هنوز چیزی نوشته نشده.</p>}/>
                                    </div>
                                    <div className="p-3 border-round-2xl" style={{background: "#f9fafb", border: "1px solid #f3f4f6"}}>
                                        <h4 className="m-0 mb-2 text-2xs font-bold" style={{color: "#e6531e"}}>دستاوردها</h4>
                                        {preview.achievements.length ? preview.achievements.map((item, index) => <div key={index} className="flex align-items-center gap-2 text-xs py-1"><span className="gu-bullet m-0" style={{background: "#e6531e", width: ".3rem", height: ".3rem"}}/>{item}</div>) : <span className="text-xs text-300">—</span>}
                                    </div>
                                    <div className="p-3 border-round-2xl" style={{background: "#fef2f24d", border: "1px solid #fee2e280"}}>
                                        <h4 className="m-0 mb-2 text-2xs font-bold" style={{color: "#dc2626"}}>چالش‌ها</h4>
                                        {preview.challenges.length ? preview.challenges.map((item, index) => <p key={index} className="m-0 text-xs py-1" style={{lineHeight: 1.9}}>{item}</p>) : <span className="text-xs text-300">—</span>}
                                    </div>
                                    <div>
                                        <h4 className="m-0 mb-2 text-2xs font-bold gu-muted pr-2" style={{borderRight: "2px solid #e5e7eb"}}>گام‌های بعدی</h4>
                                        <div className="flex flex-column gap-2">
                                            {preview.todos.length ? preview.todos.map((item, index) => (
                                                <div key={index} className="flex align-items-center gap-2 p-2 border-round-xl text-xs font-bold" style={{border: "1px solid #f3f4f6"}}><span className="inline-flex flex-none" style={{color: "#e6531e"}}><Icon icon="mi mi:check_circle" className="text-base"/></span>{item}</div>
                                            )) : <span className="text-xs text-300">—</span>}
                                        </div>
                                    </div>
                                    <div className="flex align-items-center justify-content-between pt-3" style={{borderTop: "1px solid #f3f4f6"}}>
                                        <span className="flex align-items-center gap-2 text-2xs gu-muted">
                                            {author && <Avatar memberData={author} style={{width: "1.75rem", height: "1.75rem"}} className="text-3xs"/>}
                                            گزارش از {author?.fullname || "شما"}
                                        </span>
                                        <span className="text-2xs gu-muted">{formatPersianDate(new Date())}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    );
}

export default GoalUpdateStatus;
